import PropTypes from "prop-types";
import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CampaignSetupContext } from "../../../../../context/CampaignSetupContext";
import { useOutletKeywordGroups } from "../../../../../hooks/useOutletKeywordGroups";
import RetailerSelector from "./RetailerSelector";
import { buildRetailerRuleData } from "./retailerOutcomeUtils";

/**
 * Retailer-condition wizard body (no Dialog chrome) hosted inside the
 * shared "Add condition" modal (`AddConditionModal`) as the `retailer` branch.
 *
 * Single step: pick one or more retailers from campaign.retailers.
 * On confirm it builds `ruleData` ({ keywords }) and hands it up.
 */
export default function RetailerConditionWizard({
  editData,
  onConditionAdd,
  onCancel,
  onBackToTypeSelect,
}) {
  const { t } = useTranslation("RetailerOutcome");
  const { campaignState } = useContext(CampaignSetupContext);
  const allowedRetailers = useMemo(
    () => campaignState?.retailers || [],
    [campaignState?.retailers]
  );
  const { stores } = useOutletKeywordGroups({
    skip: allowedRetailers.length === 0,
  });

  const retailerRows = useMemo(() => {
    const rowsByRetailer = new Map(
      allowedRetailers.map((retailer) => [
        retailer,
        {
          id: retailer,
          retailerName: retailer,
          storeNames: new Set(),
          storeGroups: new Set(),
        },
      ])
    );

    stores.forEach((store) => {
      const retailer = store.metadata?.retailer;
      const row = rowsByRetailer.get(retailer);
      if (!row) return;

      const storeName = store.metadata?.name || store.name;
      if (storeName) row.storeNames.add(storeName);
      if (store.category) row.storeGroups.add(store.category);
    });

    return [...rowsByRetailer.values()].map((row) => ({
      ...row,
      storeNames: [...row.storeNames].sort(),
      storeGroups: [...row.storeGroups].sort(),
    }));
  }, [allowedRetailers, stores]);

  const handleConfirm = useCallback(
    (selectedRetailers) => {
      onConditionAdd(buildRetailerRuleData(selectedRetailers));
    },
    [onConditionAdd]
  );

  // Show message if no retailers configured
  if (allowedRetailers.length === 0) {
    return (
      <p
        className="store-condition-selector__empty"
        data-testid="no-retailers-configured"
      >
        {t("NoRetailers")}
      </p>
    );
  }
  return (
    <RetailerSelector
      retailers={retailerRows}
      initialSelection={editData}
      onConfirm={handleConfirm}
      onCancel={onCancel}
      onBack={onBackToTypeSelect}
    />
  );
}

RetailerConditionWizard.propTypes = {
  onConditionAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onBackToTypeSelect: PropTypes.func,
  editData: PropTypes.arrayOf(PropTypes.string),
};

RetailerConditionWizard.defaultProps = {
  editData: null,
  onBackToTypeSelect: null,
};
